<!-- src/App.vue -->
<template>
  <div class="min-h-screen flex flex-col relative">
    <AppHeader />

    <!-- Toggle Button - Only show if not on homepage, responsive positioning -->
    <button
      v-if="!isHomePage"
      @click="toggleSidebar"
      class="fixed top-16 sm:top-20 left-3 sm:left-4 z-50 p-2 sm:p-3 bg-white shadow-lg rounded-md border hover:bg-gray-50 transition-all duration-200 lg:hover:scale-105"
      :class="{
        'left-64 sm:left-72 lg:left-80': isSidebarOpen,
        'shadow-xl': isSidebarOpen,
      }"
    >
      <svg
        class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 transition-transform duration-200"
        :class="{ 'rotate-180': isSidebarOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>

    <NavigationDrawer v-model="isSidebarOpen" />

    <!-- //TODO:Review this part -->
    <main class="pl-20 px-7 py-10">
      <!-- Main content -->
      <div class="flex-1 min-w-0">
        <router-view />
      </div>
    </main>

    <ToastContainer />

    <AppFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useRoute } from "vue-router";
import AppHeader from "@/core/components/layout/AppHeader.vue";
import AppFooter from "@/core/components/layout/AppFooter.vue";
import NavigationDrawer from "@/core/components/layout/NavigationDrawer.vue";

const route = useRoute();

// Sidebar state
const isSidebarOpen = ref(false);

// Heights for positioning
const headerHeight = ref(64); // Adjust based on your header height
const footerHeight = ref(64); // Adjust based on your footer height

// Computed property to check if current route is homepage
const isHomePage = computed(() => {
  return route.path === "/jhjvjvjvjhv"; // route.path === '/' ||
});

// Sidebar methods
const toggleSidebar = () => {
  // Double check: don't allow sidebar toggle on homepage
  if (isHomePage.value) {
    return;
  }
  isSidebarOpen.value = !isSidebarOpen.value;
};

const closeSidebar = () => {
  isSidebarOpen.value = false;
};

// Handle escape key to close sidebar
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === "Escape" && isSidebarOpen.value) {
    closeSidebar();
  }
};
// Close sidebar on route change and force close if navigating to homepage
const handleRouteChange = () => {
  closeSidebar();

  // Force close sidebar if navigating to homepage
  if (isHomePage.value) {
    isSidebarOpen.value = false;
  }
};

// Measure header and footer heights
const measureHeights = () => {
  const header = document.querySelector("header");
  const footer = document.querySelector("footer");

  if (header) {
    headerHeight.value = header.offsetHeight;
  }
  if (footer) {
    footerHeight.value = footer.offsetHeight;
  }
};

// Lifecycle hooks
onMounted(() => {
  document.addEventListener("keydown", handleEscape);
  measureHeights();

  // Re-measure heights on window resize
  window.addEventListener("resize", measureHeights);

  // Ensure sidebar is closed if starting on homepage
  if (isHomePage.value) {
    isSidebarOpen.value = false;
  }
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
  window.removeEventListener("resize", measureHeights);
});

// Watch for route changes to close sidebar
watch(() => route.path, handleRouteChange);

// Watch for homepage changes and force close sidebar
watch(isHomePage, (newValue) => {
  if (newValue) {
    isSidebarOpen.value = false;
  }
});
</script>
